<script lang="ts">
  import type { ImageAsset } from '$lib/types/contentTypes';

  export let desktopMedia: ImageAsset | undefined;
  export let desktopSizeQuery: string = 'w=1400&q=85&fm=webp';
  export let mobileMedia: ImageAsset | undefined = undefined;
  export let mobileSizeQuery: string = 'w=780&q=85&fm=webp';
  export let videoPoster: ImageAsset | undefined = undefined;
  export let alt: string = '';
  export let preload: boolean = false;
  export let mobileBreakpoint: number = 640;
  export let className: string =
    'object-cover absolute w-full h-full pointer-events-none select-none';
  export let showScrim: boolean = true;
  export let scrimOpacity: string = '40';

  const scrimOpacityClass = {
    ['10']: 'opacity-10',
    ['20']: 'opacity-20',
    ['30']: 'opacity-30',
    ['40']: 'opacity-40',
    ['50']: 'opacity-50',
    ['60']: 'opacity-60',
    ['70']: 'opacity-70',
    ['80']: 'opacity-80',
    ['90']: 'opacity-90',
  }[scrimOpacity];

  /*
  display both image and video on initial load (as cannot get initial width on server-side), then re-render to stop & play media depending on responsive resize
  */
  let windowWidth;

  $: responsiveKeySuffix = !windowWidth
    ? 'fouc'
    : windowWidth < mobileBreakpoint - 1
      ? 'desktop'
      : 'mobile';
  $: imageVisible = true;
  $: videoVisible = true;

  const desktopHeroType = desktopMedia?.contentType?.match('(video|image)')?.[0];
  const mobileHeroType = mobileMedia?.contentType?.match('(video|image)')?.[0];

  $: mobileImageSrc =
    mobileHeroType === 'image'
      ? `${mobileMedia?.url}?${mobileSizeQuery}`
      : !mobileHeroType && desktopHeroType === 'image'
        ? `${desktopMedia?.url}?${mobileSizeQuery}`
        : null;
  $: desktopImageSrc =
    desktopHeroType === 'image' ? `${desktopMedia?.url}?${desktopSizeQuery}` : null;

  $: mobileVideoSrc = mobileHeroType === 'video' ? mobileMedia?.url : null;
  $: desktopVideoSrc = desktopHeroType === 'video' ? desktopMedia?.url : null;

  $: videoPosterSrc = videoPoster?.url ? `${videoPoster.url}?${desktopSizeQuery}` : null;

  $: if (windowWidth) {
    /* if is mobile breakpoint and has mobile media */
    if (windowWidth < mobileBreakpoint - 1 && (mobileVideoSrc || mobileImageSrc)) {
      videoVisible = !!mobileVideoSrc;
      imageVisible = !videoVisible;
    } else {
      videoVisible = !!desktopVideoSrc;
      imageVisible = !videoVisible;
    }
  }
</script>

<svelte:head>
  {#if preload}
    {#if mobileImageSrc}
      <link
        rel="preload"
        as="image"
        type="image/webp"
        fetchpriority="high"
        href={mobileImageSrc}
        media={`(max-width: ${mobileBreakpoint - 1}px)`}
      />
    {/if}
    {#if desktopImageSrc}
      {#if mobileHeroType}
        <link
          rel="preload"
          as="image"
          type="image/webp"
          fetchpriority="high"
          href={desktopImageSrc}
          media={`(min-width: ${mobileBreakpoint}px)`}
        />
      {:else}
        <link
          rel="preload"
          as="image"
          type="image/webp"
          fetchpriority="high"
          href={desktopImageSrc}
        />
      {/if}
    {/if}
  {/if}
</svelte:head>

<svelte:window bind:innerWidth={windowWidth} />

{#if desktopImageSrc || mobileImageSrc}
  {#if imageVisible}
    {#key `image-${responsiveKeySuffix}`}
      <picture class={className}>
        {#if mobileImageSrc}
          <source media={`(max-width: ${mobileBreakpoint - 1}px)`} srcset={mobileImageSrc} />
        {/if}
        {#if desktopImageSrc && mobileHeroType}
          <source media={`(min-width: ${mobileBreakpoint}px)`} srcset={desktopImageSrc} />
        {:else}
          <source srcset={desktopImageSrc} />
        {/if}
        <img {alt} fetchpriority="high" loading="eager" class="h-full w-full object-cover" />
      </picture>
    {/key}
  {/if}
  {#if showScrim && scrimOpacityClass}
    <div
      class={`pointer-events-none absolute inset-0 select-none bg-black ${scrimOpacityClass}`}
    ></div>
  {/if}
{/if}
{#if desktopVideoSrc || mobileVideoSrc}
  {#if videoVisible}
    {#key `video-${responsiveKeySuffix}`}
      <video
        class={className}
        preload="metadata"
        loop
        autoplay
        muted
        playsinline
        aria-hidden="true"
        poster={videoPosterSrc}
      >
        {#if mobileVideoSrc}
          <source src={mobileVideoSrc} media={`(max-width: ${mobileBreakpoint - 1}px)`} />
        {/if}
        {#if desktopVideoSrc && mobileHeroType}
          <source src={desktopVideoSrc} media={`(min-width: ${mobileBreakpoint}px)`} />
        {:else}
          <source src={desktopVideoSrc} />
        {/if}
      </video>
    {/key}
  {/if}
  {#if showScrim}
    <div
      class={`pointer-events-none absolute inset-0 select-none bg-black ${scrimOpacityClass}`}
    ></div>
  {/if}
{/if}
